import React, { useState, useEffect, useRef } from 'react';
import { appAlert, getForbiddenCharError } from '@utils/globalConstant';
import { useStaticContent } from '@context/StaticContent';
import { sanitizeRichText } from '@utils/sanitizeHtml';
import { Notification } from '../Notification';
import './FormInputField.scss';
import Icons from '@core/Utils/Icons/Icons';
import { useActivation } from '@context/MobileOne';
import { useA11y } from '@context/Utils';

export function FormInputField({
  id,
  type,
  label,
  placeholder,
  hint,
  valid,
  invalid,
  validMessage,
  invalidMessage,
  customClass = '',
  onChange,
  onBlur,
  disabled = false,
  isOptional = false,
  showIcon = true,
  value: propValue,
  onClear,
  maxLength,
  checkCharRegex = null,
  formError,
  ...restProps
}) {
  // Refs
  const inputRef = useRef(null);

  const { getStaticContentValue } = useStaticContent();
  const { tA11yTranslate } = useA11y();

  // context
  const { setFormError } = useActivation();

  // States
  const [isFocus, setIsFocus] = useState(false);
  const [isBlur, setIsBlur] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [inputValue, setInputValue] = useState(propValue || '');
  const [isCleared, setIsCleared] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [invalidCharError, setInvalidCharError] = useState(null);

  // Functions
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const triggerChange = (event) => {
    if (onChange) {
      onChange(event);
    }
    setInputValue(event.target.value);
    setIsTouched(true);
  };

  const triggerBlur = (event) => {
    if (onBlur) {
      onBlur(event);
    }
    setIsBlur(true);
    setIsFocus(false);
    setIsTouched(false);
  };

  const handleClearInput = () => {
    setInputValue('');
    setIsCleared(true);
    if (onClear) {
      onClear(restProps.name, '');
    }
    setFormError(null); // Reset API error when input is cleared
    inputRef.current.focus();
  };

  function replaceFirstBlankOnlyEntry(input, replaceValue) {
    if (input == null) return input; // null or undefined, return as is

    const trimmedInput = input.trim();

    if (trimmedInput === '') {
      // Input is only spaces
      return replaceValue;
    }

    const parts = input.split(',').map((p) => p.trimEnd());
    let replaced = false;

    const result = parts.map((part) => {
      const trimmed = part.trim();
      // if (!replaced && trimmed === '') {
      if (!replaced && (trimmed === '' || trimmed === ',')) {
        replaced = true;
        return replaceValue;
      }
      return part.trim();
    });

    return result.join(', ');
  }

  // Hooks
  useEffect(() => {
    // Reset isCleared state when inputValue changes
    if (inputValue !== '') {
      setIsCleared(false);
    }

    // Check for invalid characters
    if (checkCharRegex) {
      const forbiddenCharError = getForbiddenCharError(inputValue, checkCharRegex);

      // Error messages for invalid characters
      const staticErrMsg = getStaticContentValue('ek_global_err_addition_character').content;
      const staticErrMsgOfSpace = getStaticContentValue(
        'ek_whitespace_additional_char_err'
      ).content; // Space char
      const staticErrMsgOfComma = getStaticContentValue('ek_comma_additional_char_err').content; // Comma char

      let invalidChars = [];

      // Check for specific invalid characters (comma and space)
      if (inputValue.includes(',') && !invalidChars.includes(staticErrMsgOfComma)) {
        invalidChars.push(staticErrMsgOfComma);
      }
      if (inputValue.includes(' ') && !invalidChars.includes(staticErrMsgOfSpace)) {
        invalidChars.push(staticErrMsgOfSpace);
      }

      // Use regex to find all other invalid characters
      const otherInvalidChars = inputValue.match(checkCharRegex);
      if (otherInvalidChars) {
        // Filter out commas and spaces from regex matches to avoid duplication
        const filteredInvalidChars = otherInvalidChars.filter(
          (char) => char !== ',' && char !== ' '
        );

        filteredInvalidChars.forEach((char) => {
          if (!invalidChars.includes(char)) {
            invalidChars.push(char);
          }
        });
      }

      // Combine all invalid characters into a single error message
      let newForbiddenCharError = invalidChars.join(', ');

      if (!newForbiddenCharError) {
        newForbiddenCharError = replaceFirstBlankOnlyEntry(forbiddenCharError, staticErrMsgOfSpace);
      }

      setInvalidCharError(forbiddenCharError ? `${staticErrMsg} ${newForbiddenCharError}` : null);
    }
  }, [inputValue]);

  return (
    <div className={`${customClass}`}>
      <label htmlFor={id} className="form-label ps-4" id={`${id}-label`}>
        {tA11yTranslate(label).isHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizeRichText(tA11yTranslate(label).content) }} />
        ) : (
          tA11yTranslate(label).content
        )}
        {isOptional && (
          <span className="ek-labeltext-12-medium text-ek-blue-placeholder ps-4">(optional)</span>
        )}
      </label>

      <div className="focus-wrapper">
        {/* input group */}
        <div
          className={`position-relative
        input-group
        ${valid ? 'valid' : ''}
        ${invalid ? 'invalid' : ''}
        ${isFocus ? 'focus' : ''}
        ${isBlur ? 'blur' : ''}
        ${isTouched ? 'touched' : ''}
        ${disabled ? 'disabled' : ''}
      `}>
          {/* input field */}
          <input
            id={id}
            ref={inputRef}
            type={type === 'password' && showPassword ? 'text' : type}
            className={`form-control z-0 ${invalid ? 'text-danger' : ' text-ek-black'}`}
            placeholder={tA11yTranslate(placeholder).content}
            value={inputValue}
            onFocus={() => {
              setIsFocus(true);
            }}
            onBlur={triggerBlur}
            onChange={triggerChange}
            disabled={disabled}
            onInput={
              maxLength !== undefined && type === 'number'
                ? (e) => {
                    if (e.target.value.length > maxLength) {
                      e.target.value = e.target.value.slice(0, maxLength);
                    }
                  }
                : null
            }
            {...restProps}
          />
          {disabled ? (
            <span
              className="icon-locked input-group-text border-0 bg-transparent p-0 pe-12"
              aria-hidden="true">
              <Icons name="LOCK" colorType="dark" />
            </span>
          ) : (
            showIcon &&
            (type === 'password' ? (
              inputValue ? (
                <div className="btn-toggle-password input-group-text border-0 bg-transparent p-0 pe-12">
                  <button
                    type="button"
                    className="btn-reset-default-style"
                    onClick={togglePasswordVisibility}>
                    <Icons
                      name={showPassword ? 'HIDE' : 'SHOW'}
                      colorType="dark"
                      alt={
                        showPassword
                          ? tA11yTranslate('ek_global_icn_hide_aria').ariaLabel
                          : tA11yTranslate('ek_global_icn_show_aria').ariaLabel
                      }
                    />
                  </button>
                </div>
              ) : (
                <></>
              )
            ) : inputValue && !isCleared ? (
              <div className="btn-clear-input input-group-text border-0 bg-transparent p-0 pe-12">
                <button
                  type="button"
                  onClick={handleClearInput}
                  className="btn-reset-default-style"
                  aria-label={tA11yTranslate('ek_global_icn_delete_aria').ariaLabel}>
                  <Icons name="TRASH" colorType="dark" />
                </button>
              </div>
            ) : (
              <></>
            ))
          )}
        </div>
      </div>
      {hint && !valid && !invalid && (
        <div id={`${id}-hint`} className="form-text pt-4 ps-4">
          <Notification message={hint} />
        </div>
      )}
      {(invalid && invalidMessage) || (checkCharRegex && invalidCharError) ? (
        <div id={restProps['aria-describedby']}>
          {invalid && invalidMessage && (
            <div id={`${id}-error`} className="form-text pt-4 ps-4">
              <Notification type={appAlert.ERROR} message={invalidMessage} />
            </div>
          )}
          {checkCharRegex && invalidCharError && (
            <div id={`${id}-error`} className="form-text pt-4 ps-4">
              <Notification type={appAlert.ERROR} message={invalidCharError} />
            </div>
          )}
        </div>
      ) : null}
      {valid && validMessage && (
        <div id={`${id}-success`} className="form-text pt-4 ps-4">
          <Notification type={appAlert.SUCCESS} message={validMessage} />
        </div>
      )}
    </div>
  );
}

export default FormInputField;
